import { HTTP } from 'framework/api';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Keeps a failed refresh distinct from a missing resource and ignores obsolete requests. */
const useDetailsResource = <Data>(
  id: string | undefined,
  fetchData: (id: string) => Promise<Data | null | undefined>,
) => {
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  const requestRef = useRef(0);
  const [state, setState] = useState<{
    id: string | undefined;
    data: Data | undefined;
    error: Error | null;
    isLoading: boolean;
  }>({ id, data: undefined, error: null, isLoading: true });

  const reload = useCallback(async () => {
    const request = ++requestRef.current;
    setState(previous => ({ id, data: previous.id === id ? previous.data : undefined, error: null, isLoading: true }));
    try {
      const data = id === undefined ? undefined : await fetchRef.current(id);
      if (request === requestRef.current) setState({ id, data: data ?? undefined, error: null, isLoading: false });
    } catch (cause) {
      if (request !== requestRef.current) return;
      const notFound =
        typeof cause === 'object' && cause !== null && 'status' in cause && Number(cause.status) === HTTP.NOT_FOUND;
      setState(previous => ({
        id,
        data: notFound ? undefined : previous.data,
        error: notFound ? null : cause instanceof Error ? cause : new Error('Could not load details.'),
        isLoading: false,
      }));
    }
  }, [id]);

  const setData = useCallback((data: Data) => {
    setState(previous => ({ ...previous, data, error: null }));
  }, []);

  useEffect(() => {
    void reload();
    return () => {
      requestRef.current += 1;
    };
  }, [reload]);

  return { ...(state.id === id ? state : { data: undefined, error: null, isLoading: true }), reload, setData };
};

export default useDetailsResource;
